import {isVideoStreamTrackProcessorAPIs} from '@pexip/media';

import {replace} from '../router';
import {infinityService} from '../services/InfinityClient.service';
import {
    EXPRESS_PATH,
    MEETING_MANAGER_PATH,
    STEP_BY_STEP_PATH,
} from '../constants';
import {logger} from '../logger';
import {config} from '../config';
import {renderUserNamePageSignal} from '../signals/MeetingFlow.signals';

import {toClientCallType} from './toClientCallTypes';

const decodeURIPart = (part: string) =>
    decodeURIComponent(part.replace(/\+/g, ' '));

export const parseLegacyParams = (legacyParams: string) => {
    try {
        const params = new URLSearchParams(legacyParams);
        const bandwidth = params.get('bandwidth');
        const conference = params.get('conference');
        const extension = params.get('extension');
        const join = params.get('join');
        const muteCamera = params.get('muteCamera');
        const muteMicrophone = params.get('muteMicrophone');
        const name = decodeURIPart(params.get('name') ?? '');
        const pin = params.get('pin');
        const role = params.get('role');
        const callTag = params.get('callTag');
        const callType = params.get('callType');
        const videoProcessingAPI = params.get('videoProcessingAPI');
        const ssoToken = params.get('ssoToken');
        const token = params.get('token');
        const allowChangeOfMediaDirection = params.get(
            'allowChangeOfMediaDirection',
        );
        const isMeetingManager = params.get('mm');

        const isGuest = role === 'guest';
        config.set({key: 'isGuest', value: isGuest});

        if (name) {
            renderUserNamePageSignal.emit(false);
            config.set({key: 'displayName', value: name});
        }

        if (bandwidth) {
            config.set({key: 'bandwidth', value: bandwidth});
        }

        if (callType) {
            config.set({key: 'callType', value: toClientCallType(callType)});
        }

        if (allowChangeOfMediaDirection) {
            if (allowChangeOfMediaDirection === 'true') {
                config.set({
                    key: 'allowChangeOfMediaDirection',
                    value: {audio: true, video: true, presentation: true},
                });
            } else {
                config.set({
                    key: 'allowChangeOfMediaDirection',
                    value: {audio: false, video: false, presentation: true},
                });
            }
        } else if (callType) {
            // Make it backward compatible to the old behavior
            config.set({
                key: 'allowChangeOfMediaDirection',
                value: {audio: false, video: false, presentation: true},
            });
        }

        // If enforceVideoMute is set, we don't want to overwrite it with the legacy params. The actual logic is in useEnforceMuteByDefault.ts which is used in MeetingFlow.page.tsx
        if (muteCamera && !config.get('enforceVideoMute')) {
            config.set({
                key: 'isVideoInputMuted',
                value: muteCamera.toLowerCase() === 'true',
            });
        }

        // If enforceVideoMute is set, we don't want to overwrite it with the legacy params. The actual logic is in useEnforceMuteByDefault.ts which is used in MeetingFlow.page.tsx
        if (muteMicrophone && !config.get('enforceAudioMute')) {
            config.set({
                key: 'isAudioInputMuted',
                value: muteMicrophone.toLowerCase() === 'true',
            });
        }

        if (isVideoStreamTrackProcessorAPIs(videoProcessingAPI)) {
            config.set({key: 'videoProcessingAPI', value: videoProcessingAPI});
        }

        if (ssoToken) {
            infinityService.setSsoToken(ssoToken ? ssoToken : undefined);
        }

        if (token) {
            infinityService.setToken(token);
        }

        if (conference) {
            let redirect = `/m/${conference}`;
            if (isMeetingManager === 'true') {
                redirect += `/${MEETING_MANAGER_PATH}`;
            } else if (!join) {
                if (isGuest) {
                    redirect += `/${STEP_BY_STEP_PATH}`;
                } else {
                    redirect += `/${EXPRESS_PATH}`;
                }
            }

            if (join && config.get('displayName')) {
                // If the name is already set and we set the join value, we can skip the name page
                renderUserNamePageSignal.emit(false);
            }
            const lng = params.get('lng');
            if (lng) {
                // Forward `lng` value so we can get this passed to i18next
                redirect += `?lng=${lng}`;
            }
            const rid = params.get('rid');
            if (rid) {
                redirect += `?rid=${rid}`;
            }
            replace(redirect);
        }

        if (pin) {
            infinityService.setPin(pin);
        }

        if (extension) {
            infinityService.setConferenceExtension(extension);
        }

        if (join === '1') {
            config.set({key: 'join', value: true});
        }

        if (callTag) {
            infinityService.setCallTag(callTag);
        }
    } catch (reason) {
        logger.debug({reason}, `Can't parse the legacy params`);
    }
};
